import type { FC, ReactNode } from 'react'
import type { CategoryRef } from '@/types'

import Link from 'next/link'
import { Facebook, Instagram, MapPin, MessageCircle, Music2 } from 'lucide-react'
import { useSettings } from '@/components/providers/settings-provider'
import { BrandMark } from '@/components/generals/logo'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'
import { buildCustomRequestMessage, buildQuestionMessage } from '@/lib/whatsapp'

interface SiteFooterProps {
    categories: Array<CategoryRef>
}

// Turns "@ema.charms", "ema.charms" or a full link into a URL for that network.
const socialUrl = (value: string, base: string): string => {
    const trimmed = value.trim()

    if (/^https?:\/\//i.test(trimmed)) return trimmed

    return `${base}${trimmed.replace(/^@/, '')}`
}

export const SiteFooter: FC<SiteFooterProps> = ({ categories }): ReactNode => {
    const settings = useSettings()
    const tag = (column: string) => (settings.id ? `store_settings:${settings.id}:${column}` : undefined)

    const socials = [
        settings.instagram && { label: 'Instagram', href: socialUrl(settings.instagram, 'https://instagram.com/'), icon: Instagram },
        settings.tiktok && { label: 'TikTok', href: socialUrl(settings.tiktok, 'https://www.tiktok.com/@'), icon: Music2 },
        settings.facebook && { label: 'Facebook', href: socialUrl(settings.facebook, 'https://facebook.com/'), icon: Facebook }
    ].filter((item): item is { label: string, href: string, icon: typeof Instagram } => Boolean(item))

    return (
        <footer className='relative overflow-hidden bg-cocoa-900 text-cream-100'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-[repeating-linear-gradient(90deg,#e8a5b0_0_10px,transparent_10px_18px)] opacity-60' />
            <div aria-hidden='true' className='pointer-events-none absolute -right-32 -top-32 size-[420px] rounded-full bg-blush-500/10 blur-3xl' />

            <div className='relative mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1.5fr_1fr_1fr_1.1fr] lg:py-20'>
                <div className='max-w-sm'>
                    <div className='flex items-center gap-4'>
                        <BrandMark size={96} className='ring-4 ring-white/10' />
                        <div>
                            <p className='font-heading text-2xl font-semibold tracking-[0.04em] text-cream-50 font-soft'>
                                <span data-mdlfy-cms={tag('store_name')}>{settings.storeName}</span>
                            </p>
                            {settings.tagline && (
                                <p className='mt-1 font-heading text-base italic text-blush-200'>
                                    <span data-mdlfy-cms={tag('tagline')}>{settings.tagline}</span>
                                </p>
                            )}
                            {settings.subtagline && (
                                <p className='mt-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-cream-200/70'>
                                    <span data-mdlfy-cms={tag('subtagline')}>{settings.subtagline}</span>
                                </p>
                            )}
                        </div>
                    </div>
                    {settings.description && (
                        <p className='mt-6 text-sm leading-relaxed text-cream-200/80'>
                            <span data-mdlfy-cms={tag('description')}>{settings.description}</span>
                        </p>
                    )}
                    <WhatsAppLink message={buildQuestionMessage} className='mt-6' size='sm'>
                        Chat lewat WhatsApp
                    </WhatsAppLink>
                </div>

                <nav aria-labelledby='footer-shop'>
                    <p id='footer-shop' className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Belanja
                    </p>
                    <ul className='mt-4 space-y-2.5 text-sm'>
                        <li>
                            <Link href='/shop' className='text-cream-200/85 transition-colors hover:text-white'>
                                Semua Produk
                            </Link>
                        </li>
                        {categories.map(category => (
                            <li key={category.id}>
                                <Link href={`/shop?category=${category.slug}`} className='text-cream-200/85 transition-colors hover:text-white'>
                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                        {category.name}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-labelledby='footer-explore'>
                    <p id='footer-explore' className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Jelajahi
                    </p>
                    <ul className='mt-4 space-y-2.5 text-sm'>
                        <li>
                            <Link href='/' className='text-cream-200/85 transition-colors hover:text-white'>
                                Butik 3D
                            </Link>
                        </li>
                        <li>
                            <Link href='/shop?label=terbaru' className='text-cream-200/85 transition-colors hover:text-white'>
                                Produk Terbaru
                            </Link>
                        </li>
                        <li>
                            <Link href='/shop?label=terlaris' className='text-cream-200/85 transition-colors hover:text-white'>
                                Produk Terlaris
                            </Link>
                        </li>
                        <li>
                            <Link href='/#story' className='text-cream-200/85 transition-colors hover:text-white'>
                                Cara kami merajut
                            </Link>
                        </li>
                        <li>
                            <Link href='/#how-to-order' className='text-cream-200/85 transition-colors hover:text-white'>
                                Cara memesan
                            </Link>
                        </li>
                        <li>
                            <Link href='/wishlist' className='text-cream-200/85 transition-colors hover:text-white'>
                                Favorit
                            </Link>
                        </li>
                    </ul>
                </nav>

                <div>
                    <p className='text-xs font-bold uppercase tracking-[0.22em] text-blush-200'>
                        Hubungi kami
                    </p>
                    <ul className='mt-4 space-y-3 text-sm text-cream-200/85'>
                        {settings.address && (
                            <li className='flex gap-2.5'>
                                <MapPin className='mt-0.5 size-4 shrink-0 text-blush-200' />
                                <span className='whitespace-pre-line'>
                                    <span data-mdlfy-cms={tag('address')}>{settings.address}</span>
                                </span>
                            </li>
                        )}
                        <li>
                            <WhatsAppLink message={buildQuestionMessage} variant='ghost' size='sm' className='-ml-4 text-cream-100 hover:bg-white/5'>
                                Tanya stok dan ongkir
                            </WhatsAppLink>
                        </li>
                        <li>
                            <WhatsAppLink message={buildCustomRequestMessage} variant='ghost' size='sm' className='-ml-4 text-blush-100 hover:bg-white/5'>
                                Pesan boneka custom
                            </WhatsAppLink>
                        </li>
                    </ul>
                    {socials.length > 0 && (
                        <ul className='mt-5 flex gap-2' aria-label='Media sosial'>
                            {socials.map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={item.label}
                                            title={item.label}
                                            className='grid size-10 place-items-center rounded-full bg-white/8 text-cream-100 transition hover:bg-white/15'
                                        >
                                            <Icon className='size-4' />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                    {!socials.length && !settings.address && (
                        <p className='mt-4 flex items-center gap-2 text-xs text-cream-200/60'>
                            <MessageCircle className='size-4' />
                            Balasan tercepat lewat WhatsApp
                        </p>
                    )}
                </div>
            </div>

            <div className='relative border-t border-white/10'>
                <div className='mx-auto flex max-w-7xl flex-col gap-2 px-5 py-6 text-xs text-cream-200/60 sm:flex-row sm:items-center sm:justify-between sm:px-8'>
                    <p>
                        <span suppressHydrationWarning>
                            {new Date().getFullYear()}
                        </span>
                        {' '}
                        <span data-mdlfy-cms={tag('store_name')}>{settings.storeName}</span>
                        <span>. Semua produk dibuat dengan tangan.</span>
                    </p>
                    <p className='flex items-center gap-4'>
                        {settings.tagline && (
                            <span className='font-heading italic text-cream-200/80'>
                                <span data-mdlfy-cms={tag('tagline')}>{settings.tagline}</span>
                            </span>
                        )}
                        <Link href='/admin' className='text-cream-200/40 transition hover:text-cream-100'>
                            Admin
                        </Link>
                    </p>
                </div>
            </div>
        </footer>
    )
}
